import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AssembliesToBuildPanel } from '../components/AssembliesToBuildPanel';
import { Network, Package, Layers, Puzzle, ChevronRight, ChevronDown, FolderTree, AlertTriangle } from 'lucide-react';

const KIND = {
  family:   { Icon: FolderTree, cls: 'text-violet-700 bg-violet-50 border-violet-200', label: 'Family' },
  product:  { Icon: Package,    cls: 'text-orange-700 bg-orange-50 border-orange-200', label: 'Product' },
  assembly: { Icon: Layers,     cls: 'text-sky-700 bg-sky-50 border-sky-200',          label: 'Assembly' },
  part:     { Icon: Puzzle,     cls: 'text-slate-600 bg-slate-50 border-slate-200',    label: 'Part' },
};

const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const CostChip = ({ n, testId }) => (n.rolled_material_cost == null ? null : (
  <span className="text-[10px] mono px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-600" title="Rolled-up materials · materials + labour" data-testid={testId}>
    {money(n.rolled_material_cost)} · {money((n.rolled_material_cost || 0) + (n.rolled_labor_cost || 0))} w/ labour{n.missing_price_count > 0 ? ' ⚠' : ''}
  </span>
));

const RevChip = ({ node, onOpen }) => {
  if (!node.bom_id) return <span className="text-[10px] text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />no BOM</span>;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(node.bom_id); }} className={`text-[10px] font-mono px-1.5 py-0.5 rounded border hover:underline ${node.bom_status === 'Released' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-testid={`structure-bom-link-${node.bom_id}`}>
      rev {node.bom_revision} · {node.bom_status}
    </button>
  );
};

const Row = ({ kind, title, sub, right, depth, open, hasKids, onToggle, testId }) => {
  const { Icon, cls, label } = KIND[kind];
  return (
    <div className="flex items-center gap-2 py-1.5 pr-3 hover:bg-slate-50 rounded-sm cursor-pointer transition-colors" style={{ paddingLeft: depth * 20 + 4 }} onClick={onToggle} data-testid={testId}>
      <span className="w-4 h-4 text-slate-400 shrink-0">{hasKids ? (open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}</span>
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border shrink-0 ${cls}`}><Icon className="w-3 h-3" />{label}</span>
      <span className="text-sm text-slate-800 truncate">{title}</span>
      {sub && <span className="text-xs text-slate-500 truncate">{sub}</span>}
      <span className="ml-auto flex items-center gap-2 shrink-0">{right}</span>
    </div>
  );
};

const LineNode = ({ n, depth, onOpen }) => {
  const [open, setOpen] = useState(depth < 3);
  const isAsm = n.kind === 'assembly';
  const kids = n.children || [];
  return (
    <>
      <Row
        kind={n.kind} depth={depth} open={open} hasKids={isAsm && kids.length > 0} onToggle={() => setOpen(o => !o)}
        title={isAsm ? n.assembly_name : (n.item_name || n.component_sku)}
        sub={<span className="mono">{n.component_sku} × {n.quantity_per_unit} {n.uom}</span>}
        right={<>{isAsm && <RevChip node={n} onOpen={onOpen} />}<span className={`text-[11px] mono ${n.on_hand > 0 ? 'text-slate-500' : 'text-rose-600'}`}>{n.on_hand ?? '—'} on hand</span></>}
        testId={`structure-line-${n.component_sku}-${depth}`}
      />
      {isAsm && open && kids.map((c, i) => React.createElement(LineNode, { key: `${c.component_sku}-${i}`, n: c, depth: depth + 1, onOpen }))}
    </>
  );
};

const ProductNode = ({ p, depth, onOpen }) => {
  const [open, setOpen] = useState(true);
  const asm = p.children.filter(c => c.kind === 'assembly').length;
  return (
    <>
      <Row kind="product" depth={depth} open={open} hasKids={p.children.length > 0} onToggle={() => setOpen(o => !o)}
        title={p.name} sub={`${asm} assembl${asm === 1 ? 'y' : 'ies'} · ${p.children.length - asm} loose part${p.children.length - asm === 1 ? '' : 's'}`}
        right={<><CostChip n={p} testId={`structure-product-cost-${p.id}`} /><RevChip node={p} onOpen={onOpen} /></>} testId={`structure-product-${p.id}`} />
      {open && p.children.map((c, i) => <LineNode key={`${c.component_sku}-${i}`} n={c} depth={depth + 1} onOpen={onOpen} />)}
    </>
  );
};

const FamilyNode = ({ f, onOpen }) => {
  const [open, setOpen] = useState(true);
  return (
    <>
      <Row kind="family" depth={0} open={open} hasKids={f.products.length > 0} onToggle={() => setOpen(o => !o)}
        title={f.name} sub={`${f.product_count} product${f.product_count === 1 ? '' : 's'}`} testId={`structure-family-${f.id}`} />
      {open && f.products.map(p => <ProductNode key={p.id} p={p} depth={1} onOpen={onOpen} />)}
    </>
  );
};

const AssemblyLibrary = ({ assemblies }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [whereFor, setWhereFor] = useState(null);
  const [rows, setRows] = useState([]);
  const openWhere = (a) => { setWhereFor(a); setRows([]); api().get(`/structure/where-used/${encodeURIComponent(a.item_sku)}`).then(r => setRows(r.data)).catch(() => {}); };
  return (
    <Card className="industrial-card p-4 space-y-2" data-testid="structure-assembly-library">
      <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Layers className="w-4 h-4 text-sky-600" />Assembly library</h2>
      {assemblies.length === 0 && <p className="text-xs text-slate-500">No assemblies yet. Add one under Products &amp; Assemblies.</p>}
      {assemblies.map(a => (
        <button key={a.id} type="button" onClick={() => openWhere(a)} className="w-full text-left rounded-sm border border-slate-200 px-3 py-2 hover:border-sky-300 hover:bg-sky-50/40 transition-colors" data-testid={`structure-assembly-${a.item_sku}`}>
          <div className="flex items-center justify-between gap-2"><span className="text-sm font-medium text-slate-800 truncate">{a.name}</span><span className="text-[11px] mono text-slate-500">{a.on_hand} on hand</span></div>
          <div className="text-[11px] text-slate-500 mono">{a.item_sku} · used in {a.used_in_boms} BOM{a.used_in_boms === 1 ? '' : 's'}</div>
          <div className="mt-1"><CostChip n={a} testId={`structure-assembly-cost-${a.item_sku}`} /></div>
        </button>
      ))}
      <Dialog open={!!whereFor} onOpenChange={(v) => !v && setWhereFor(null)}>
        <DialogContent className="max-w-md" data-testid="structure-where-used-dialog">
          <DialogHeader><DialogTitle>Where is {whereFor?.name} used?</DialogTitle></DialogHeader>
          {rows.length === 0 ? <p className="text-sm text-slate-500">Not used in any active BOM yet.</p> : rows.map(r => (
            <button key={r.id} type="button" onClick={() => navigate(`/boms/view/${r.id}`)} className="w-full text-left rounded-sm border border-slate-200 px-3 py-2 hover:bg-slate-50 text-sm" data-testid={`where-used-row-${r.id}`}>
              <span className={`text-[10px] uppercase mr-2 px-1.5 py-0.5 rounded border ${KIND[r.level === 'assembly' ? 'assembly' : 'product'].cls}`}>{r.level}</span>
              {r.name} <span className="mono text-slate-500">· {r.bom_variant_code} rev {r.bom_revision} · {r.status}</span>
            </button>
          ))}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default function ProductStructurePage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api().get('/structure/tree').then(r => setData(r.data)).catch(() => setData({ families: [], unassigned_products: [], assemblies: [], counts: {} })); }, [api]);
  const openBom = (id) => navigate(`/boms/view/${id}`);
  const c = data?.counts || {};

  return (
    <div className="space-y-6 animate-slideIn" data-testid="product-structure-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Network className="w-6 h-6 text-orange-600" />Product Structure</h1>
        <p className="text-slate-500 text-sm">Family → Products → Assemblies → Parts, exploded from each item's latest Released BOM (or latest draft if none is released).</p>
      </div>
      <div className="flex gap-3 flex-wrap" data-testid="structure-counts">
        {[['family', c.families], ['product', c.products], ['assembly', c.assemblies]].map(([k, v]) => {
          const { Icon, cls, label } = KIND[k];
          return <div key={k} className={`flex items-center gap-2 rounded-sm border px-4 py-2 ${cls}`}><Icon className="w-4 h-4" /><span className="text-xl font-semibold font-mono">{v ?? 0}</span><span className="text-xs uppercase tracking-wide">{{ Family: 'Families', Product: 'Products', Assembly: 'Assemblies' }[label]}</span></div>;
        })}
      </div>
      <AssembliesToBuildPanel />
      {!data ? <div className="flex items-center justify-center h-48"><div className="spinner" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="industrial-card p-3 lg:col-span-2 space-y-0.5" data-testid="structure-tree">
            {data.families.map(f => <FamilyNode key={f.id} f={f} onOpen={openBom} />)}
            {data.unassigned_products.length > 0 && <p className="text-[11px] uppercase tracking-wide text-slate-400 pt-3 pb-1 px-1">Products not in a family</p>}
            {data.unassigned_products.map(p => <ProductNode key={p.id} p={p} depth={0} onOpen={openBom} />)}
            {!data.families.length && !data.unassigned_products.length && <p className="text-sm text-slate-500 p-4" data-testid="structure-empty">No products yet.</p>}
          </Card>
          <AssemblyLibrary assemblies={data.assemblies} />
        </div>
      )}
    </div>
  );
}
